import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import dayjs from "dayjs";
import { IoClose } from "react-icons/io5";
import { FaMinus, FaPlus } from "react-icons/fa";
import PhoneInputField from "@/Common/PhoneInputField";
import DatePickerField from "@/Common/DatePickerField";
import useIsDesktop from "@/hooks/useIsDesktop";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import { submitEnquiryForm } from "@/services/api/common";
import { formatIndianPrice } from "@/utils/formatIndianPrice";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import {
  ENQUIRY_MODULE,
  buildEnquirySubmitParams,
  createEnquiryFormState,
  isEnquirySuccess,
  saveEnquiryReturnUrl,
  validateEnquiryForm,
} from "@/utils/enquiryForm";
import { withRegionPath } from "@/utils/session";
import styles from "./EnquiryModal.module.css";

function CounterField({
  label,
  value,
  onMinus,
  onPlus,
  min = 0,
  max = 99,
  error = "",
}) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <div className={`${styles.counter} ${error ? styles.inputError : ""}`}>
        <button type="button" aria-label={`Decrease ${label}`} onClick={onMinus} disabled={value <= min}>
          <FaMinus size={12} />
        </button>
        <span>{value}</span>
        <button
          type="button"
          className={styles.plus}
          aria-label={`Increase ${label}`}
          onClick={onPlus}
          disabled={value >= max}
        >
          <FaPlus size={12} />
        </button>
      </div>
      {error ? <span className={styles.errorText}>{error}</span> : null}
    </div>
  );
}

export default function EnquiryModal({
  open,
  onClose,
  moduleType = ENQUIRY_MODULE.HOLIDAY,
  title = "",
  subtitle = "",
  badgeLabel = "",
  image = "",
  price = 0,
  cutPrice = 0,
  discountTag = "",
  pageUrlName = "",
  defaultNights = 2,
  defaultTravelers = 2,
}) {
  const router = useRouter();
  const { isDesktop } = useIsDesktop(900);
  const isHoliday = moduleType === ENQUIRY_MODULE.HOLIDAY;

  const [form, setForm] = useState(() =>
    createEnquiryFormState({ nights: defaultNights, travelers: defaultTravelers })
  );
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useBodyScrollLock(open);

  const minDate = dayjs().add(1, "day").format("YYYY-MM-DD");
  const showDiscount = cutPrice > price;
  const savePercent = showDiscount && cutPrice
    ? Math.round(((cutPrice - price) / cutPrice) * 100)
    : 0;

  useEffect(() => {
    if (!open) return undefined;
    saveEnquiryReturnUrl();
    setForm(createEnquiryFormState({ nights: defaultNights, travelers: defaultTravelers }));
    setErrors({});
    setLoading(false);

    return undefined;
  }, [open, defaultNights, defaultTravelers]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePhoneChange = (value) => {
    setForm((prev) => ({ ...prev, phone: value }));
    setErrors((prev) => ({ ...prev, phone: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validateEnquiryForm(form, { moduleType });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const params = buildEnquirySubmitParams({
      form,
      moduleType,
      pageUrlName: pageUrlName || title,
      url: typeof window !== "undefined" ? window.location.href : "",
    });

    if (!params) {
      setErrors((prev) => ({ ...prev, phone: "Enter a valid mobile number" }));
      return;
    }

    setLoading(true);
    try {
      const response = await submitEnquiryForm(params);
      if (!isEnquirySuccess(response)) {
        throw new Error(response?.message || "Submission failed");
      }
      onClose?.();
      router.push(withRegionPath("/thank-you"));
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to send enquiry. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`${styles.root} ${isDesktop ? styles.rootDesktop : styles.rootMobile}`}
      role="presentation"
    >
      <button
        type="button"
        className={styles.overlay}
        aria-label="Close enquiry modal"
        onClick={onClose}
      />
      <div
        className={`${styles.modal} ${isDesktop ? styles.modalDesktop : styles.modalMobile}`}
        role="dialog"
        aria-modal="true"
        aria-label="Send enquiry"
      >
        <div className={styles.modalTopBar}>
          <button type="button" className={styles.closeBtn} aria-label="Close" onClick={onClose}>
            <IoClose size={22} />
          </button>
        </div>

        <div className={styles.headerCard}>
          <div className={styles.summary}>
            <div className={styles.packageRow}>
              <div className={styles.thumb}>
                {image ? <img src={image} alt={title || "Package"} /> : null}
              </div>
              <div className={styles.packageInfo}>
                <h2 className={styles.title}>{title}</h2>
                {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
                {badgeLabel ? <span className={styles.slab}>{badgeLabel}</span> : null}
              </div>
            </div>
            <div className={styles.priceAside}>
              {showDiscount ? (
                <p className={styles.cutPrice}>
                  {getCurrencySymbol("sub", 10)}
                  {formatIndianPrice(cutPrice)}
                </p>
              ) : null}
              <p className={styles.priceNow}>
                {getCurrencySymbol("main", 15)}
                {formatIndianPrice(price)}
              </p>
              {discountTag || savePercent ? (
                <span className={styles.discountPill}>
                  {discountTag || `Get Flat ${savePercent}% OFF*`}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>First Name</span>
              <input
                type="text"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                className={`${styles.input} ${errors.firstName ? styles.inputError : ""}`}
                placeholder="First Name"
              />
              {errors.firstName ? <span className={styles.errorText}>{errors.firstName}</span> : null}
            </label>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>Last Name</span>
              <input
                type="text"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                className={`${styles.input} ${errors.lastName ? styles.inputError : ""}`}
                placeholder="Last Name"
              />
              {errors.lastName ? <span className={styles.errorText}>{errors.lastName}</span> : null}
            </label>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Phone Number</span>
              <PhoneInputField
                name="phone"
                value={form.phone}
                onChange={handlePhoneChange}
                error={Boolean(errors.phone)}
                className={styles.phoneField}
              />
              {errors.phone ? <span className={styles.errorText}>{errors.phone}</span> : null}
            </div>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>Email ID</span>
              <input
                type="email"
                name="emailId"
                value={form.emailId}
                onChange={handleChange}
                className={`${styles.input} ${errors.emailId ? styles.inputError : ""}`}
                placeholder="Email Address"
              />
              {errors.emailId ? <span className={styles.errorText}>{errors.emailId}</span> : null}
            </label>

            {isHoliday ? (
              <>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Travel Date</span>
                  <div className={styles.dateWrap}>
                    <DatePickerField
                      value={form.travelDate}
                      minDate={minDate}
                      onChange={(value) =>
                        setForm((prev) => ({ ...prev, travelDate: value }))
                      }
                      error={Boolean(errors.travelDate)}
                    />
                  </div>
                  {errors.travelDate ? (
                    <span className={styles.errorText}>{errors.travelDate}</span>
                  ) : null}
                </label>

                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Flight Availability</span>
                  <select
                    name="flightAvailability"
                    value={form.flightAvailability}
                    onChange={handleChange}
                    className={`${styles.input} ${styles.select} ${
                      errors.flightAvailability ? styles.inputError : ""
                    }`}
                  >
                    <option value="">Select</option>
                    <option value="with">With Flight</option>
                    <option value="without">Without Flight</option>
                  </select>
                  {errors.flightAvailability ? (
                    <span className={styles.errorText}>{errors.flightAvailability}</span>
                  ) : null}
                </label>

                <CounterField
                  label="No. of Nights"
                  value={form.nights}
                  min={1}
                  max={30}
                  error={errors.nights}
                  onMinus={() =>
                    setForm((prev) => ({
                      ...prev,
                      nights: Math.max(1, Number(prev.nights) - 1),
                    }))
                  }
                  onPlus={() =>
                    setForm((prev) => ({
                      ...prev,
                      nights: Math.min(30, Number(prev.nights) + 1),
                    }))
                  }
                />

                <CounterField
                  label="No. of Travelers"
                  value={form.travelers}
                  min={1}
                  max={20}
                  error={errors.travelers}
                  onMinus={() =>
                    setForm((prev) => ({
                      ...prev,
                      travelers: Math.max(1, Number(prev.travelers) - 1),
                    }))
                  }
                  onPlus={() =>
                    setForm((prev) => ({
                      ...prev,
                      travelers: Math.min(20, Number(prev.travelers) + 1),
                    }))
                  }
                />
              </>
            ) : null}
          </div>

          <button type="submit" className={styles.submitBtn} disabled={loading}>
            {loading ? "Submitting..." : "Submit Enquiry"}
          </button>
        </form>
      </div>
    </div>
  );
}

export { ENQUIRY_MODULE };
